import { stringify } from "query-string";
import { format } from "date-fns";
import { instance } from "../instance";
import { IAxiosError } from "../interfaces";
import { getError } from "../utils";

import {
  GameInput, UpdateGameRequest, DocumentInput, OperatorGamePopulated,
  GetGameListRequest, SetFinishDateRequest, BasicResponse, GameFinalWinner,
} from "../../shared/interfaces";
import { PATH } from "../../shared/constants";
import { normalizeOperatorEmail } from "../../utils/operatorEmail";

function withLocalStartDateWeekday<T extends OperatorGamePopulated>(game: T): T {
  return {
    ...game,
    startDateWeekday: format(new Date(game.startDate), "EEEE"),
  };
}

export interface GameChecklistGameStatsClient {
  totalPublishedMoments: number;
  avgPhotosPerMoment: number;
  firstPublishedMomentMs: number | null;
  dataCaptionCoverage: { caption: string; covered: boolean }[];
}

export type MomentoPromotionChannel = "videoboard" | "mobile_app" | "signage";

export type MomentoPromotionPhoto = {
  channel: MomentoPromotionChannel;
  key: string;
  originalName?: string;
  viewUrl?: string;
};

export interface GameChecklistPayload {
  exists: boolean;
  checklist: null | {
    companionCamerasOff: boolean;
    companionAutoFocusOff: boolean;
    hadProcessIssues: boolean;
    processIssuesDetail: string;
    highlightsAndFeedback: string;
    submittedByEmail: string;
    /** From Manage Access profile; falls back to email on older servers. */
    submittedByDisplayName?: string;
    submittedAt: string;
    finalTeam1Score?: number;
    finalTeam2Score?: number;
    finalWinner?: GameFinalWinner;
    parkingCost?: number;
    parkingReceiptKey?: string;
    parkingReceiptUrl?: string;
    momentoPromotionPhotos?: MomentoPromotionPhoto[];
    /** When super admin corrected scores on the game vs. checklist submission */
    checklistReportedFinal?: {
      finalTeam1Score: number;
      finalTeam2Score: number;
      finalWinner: GameFinalWinner;
    };
  };
  gameStats: GameChecklistGameStatsClient;
}

export interface SubmitGameChecklistBody {
  viewerEmail: string;
  companionCamerasOff: boolean;
  companionAutoFocusOff: boolean;
  hadProcessIssues: boolean;
  processIssuesDetail?: string;
  highlightsAndFeedback?: string;
  finalTeam1Score: number;
  finalTeam2Score: number;
  finalWinner: GameFinalWinner;
  parkingCost?: number;
  parkingReceiptKey?: string;
  parkingReceiptUrl?: string;
  /** Finish without a receipt photo; email a secure upload link instead. */
  requestParkingExpenseEmail?: boolean;
  momentoPromotionPhotos?: Array<{
    channel: MomentoPromotionChannel;
    key: string;
    originalName?: string;
  }>;
}

export const getGamesPopulatedRequest = async (
  request: GetGameListRequest,
): Promise<OperatorGamePopulated[]> => {
  const params = stringify(request, { skipNull: true, skipEmptyString: true });

  try {
    const response = await instance.get(`${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}?${params}`);

    return (response.data as OperatorGamePopulated[]).map(withLocalStartDateWeekday);
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export type LiveGameViewMomentRow = {
  id: string;
  name: string;
  cameraCount: number;
  imageCount: number;
  startTime: number;
  cameras: string[];
};

export type LiveGameViewDataMoment = {
  id: string;
  name: string;
  cameras: string[];
  cameraCount?: number;
  captured?: boolean;
};

export type LiveGameViewCard = {
  gameId: string;
  team1Id?: string;
  team1Name: string;
  team1Logo: string;
  team2Name: string;
  team2Logo: string;
  stadiumName: string;
  startDate: number;
  startTime: string;
  cityName?: string;
  timezone?: string;
  timezoneAbbr?: string;
  kickoffAt?: number;
  status: "ongoing" | "upcoming" | "finished" | "archived";
  team1Score?: number;
  team2Score?: number;
  publishedCount: number;
  lastPublishedAt: number | null;
  lastPublishedCameraCount: number;
  dataCaptured: number;
  dataTotal: number;
  dataScript?: { caption: string; covered: boolean }[];
  dataMoments?: LiveGameViewDataMoment[];
  uniqueUsers?: number;
  downloads?: number;
  ticketTemplateSource?: "game" | "default" | "none";
  ticketTemplateId?: string | null;
  eventOperator?: { email: string; name: string } | null;
  publishedMoments: LiveGameViewMomentRow[];
};

export type LiveGameViewResponse = {
  date: number;
  games: LiveGameViewCard[];
};

export const getLiveGameViewRequest = async (
  viewerEmail: string,
  dateMs: number,
): Promise<LiveGameViewResponse> => {
  try {
    const params = stringify({ viewerEmail, date: dateMs });
    const response = await instance.get<LiveGameViewResponse>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/live-game-view?${params}`,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export type GameWeatherForecast = {
  city: string;
  state?: string;
  timezone: string;
  atMs: number;
  temperatureF: number;
  precipitationChancePct: number;
  weatherCode: number;
  summary: string;
  icon: string;
  sunsetMs?: number | null;
  frozen?: boolean;
};

export const getGameWeatherByGameId = async (
  gameId: string,
): Promise<GameWeatherForecast | null> => {
  try {
    const response = await instance.get(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${encodeURIComponent(gameId)}/weather`,
    );
    return (response.data?.forecast as GameWeatherForecast | null) || null;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getGameWeatherForecast = async (params: {
  city: string;
  state?: string;
  startDateMs: number;
  startTime?: string;
  gameId?: string;
}): Promise<GameWeatherForecast | null> => {
  const query = stringify({
    gameId: params.gameId || undefined,
    city: params.city,
    state: params.state || undefined,
    startDateMs: params.startDateMs,
    startTime: params.startTime || undefined,
  }, { skipNull: true, skipEmptyString: true });

  try {
    const response = await instance.get(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/weather?${query}`,
    );
    return (response.data?.forecast as GameWeatherForecast | null) || null;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getGameDataByIdRequest = async (gameId: string): Promise<OperatorGamePopulated> => {
  try {
    const response = await instance.get(`${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${gameId}`);

    return withLocalStartDateWeekday(response.data as OperatorGamePopulated);
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export interface GameHubTimelinePoint {
  t: string;
  registrations: number;
  downloads: number;
  sessions: number;
  momentosSeen: number;
}

export type HubEngagementChartPreset =
  "24h" | "48h" | "72h" | "14d" | "full" | "custom";

export interface HubEngagementChartRangeMeta {
  preset: string;
  fromMs: number;
  toMs: number;
}

export interface GameHubDashboardPayload {
  game: OperatorGamePopulated;
  stats: {
    uniqueRegistrations: number;
    sessions: number;
    downloadedPhotos: number;
    ticketsDownloaded: number;
    momentosSeen: number;
    optOuts: number;
    /** In-venue send-to-print covers for this game. Omitted from the hub when false. */
    showInVenuePrints?: boolean;
    /** Printed covers (and photo products) sent to this game's printer folders. */
    printedPhotosCovers?: number;
    /** Printed tickets sent to this game's printer folders. */
    printedTickets?: number;
  };
  topByDownloads: Array<{
    email: string;
    count: number;
    displayName: string;
    avatarUrl?: string;
  }>;
  topBySessions: Array<{
    email: string;
    count: number;
    displayName: string;
    avatarUrl?: string;
  }>;
  imageDownloadPool: Array<{
    id: string;
    previewUrl?: string;
    created: string;
    pinned?: boolean;
    pinnedOrder?: number;
  }>;
  ticketDownloadPool: Array<{
    id: string;
    previewUrl?: string;
    created: string;
    pinned?: boolean;
    pinnedOrder?: number;
  }>;
  imageDownloadHasMore?: boolean;
  ticketDownloadHasMore?: boolean;
  timeline72h: GameHubTimelinePoint[];
  /** Echo of the window used for hourly engagement buckets (Chicago time buckets). */
  engagementChartRange?: HubEngagementChartRangeMeta;
  publishedMoments: Array<{ _id?: string; description: string; momentStart?: number }>;
  checklist: null | {
    companionCamerasOff?: boolean;
    companionAutoFocusOff?: boolean;
    hadProcessIssues?: boolean;
    processIssuesDetail?: string;
    highlightsAndFeedback?: string;
    submittedAt?: string;
    submittedByEmail?: string;
    momentoPromotionPhotos?: MomentoPromotionPhoto[];
  };
  supportQuestions: Array<{
    email?: string;
    message?: string;
    status?: string;
    createdAt?: string;
    seatContext?: string;
  }>;
  gameStats: GameChecklistGameStatsClient | null;
  dataMomentCaptions: string[];
  hubSpotlights?: Array<{
    id: string;
    previewUrl?: string;
    uploadedBy?: string;
    createdAt: string;
  }>;
  organicSocialPosts?: Array<{
    id: string;
    previewUrl?: string;
    uploadedBy?: string;
    createdAt: string;
    source: "upload" | "promotion";
  }>;
}

export const uploadGameHubSpotlightsRequest = async (
  gameId: string,
  viewerEmail: string,
  files: File[],
): Promise<{ success: boolean; added: number }> => {
  const e = normalizeOperatorEmail(viewerEmail);
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  try {
    const response = await instance.post<{ success: boolean; added: number }>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${encodeURIComponent(gameId)}/hub-spotlight-upload`,
      formData,
      { params: { viewerEmail: e || undefined } },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const uploadGameHubOrganicSocialRequest = async (
  gameId: string,
  viewerEmail: string,
  files: File[],
): Promise<{ success: boolean; added: number }> => {
  const e = normalizeOperatorEmail(viewerEmail);
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  const path = `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}`
    + `/${encodeURIComponent(gameId)}/hub-organic-social-upload`;
  try {
    const response = await instance.post<{ success: boolean; added: number }>(
      path,
      formData,
      { params: { viewerEmail: e || undefined } },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const deleteGameHubOrganicSocialRequest = async (
  gameId: string,
  viewerEmail: string,
  postId: string,
): Promise<void> => {
  const e = normalizeOperatorEmail(viewerEmail);
  const path = `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}`
    + `/${encodeURIComponent(gameId)}/hub-organic-social/${encodeURIComponent(postId)}`;
  try {
    await instance.delete(path, { params: { viewerEmail: e || undefined } });
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const pinHubShowcaseDownloadRequest = async (
  gameId: string,
  viewerEmail: string,
  album: "image" | "ticket" | "gif",
  downloadId: string,
  pinned: boolean,
): Promise<void> => {
  const e = normalizeOperatorEmail(viewerEmail);
  try {
    await instance.put(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${encodeURIComponent(gameId)}/hub-showcase-pin`,
      { album, downloadId, pinned },
      { params: { viewerEmail: e || undefined } },
    );
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getHubShowcasePinnedKeysRequest = async (
  gameId: string,
  viewerEmail: string,
): Promise<{
  imageKeys: string[];
  ticketKeys: string[];
  gifKeys: string[];
  allKeys: string[];
}> => {
  const e = normalizeOperatorEmail(viewerEmail);
  try {
    const response = await instance.get(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${encodeURIComponent(gameId)}/hub-showcase-pinned-keys`,
      { params: { viewerEmail: e || undefined } },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const pinHubShowcaseByS3KeyRequest = async (
  gameId: string,
  viewerEmail: string,
  s3Key: string,
  pinned: boolean,
  album?: "image" | "ticket" | "gif",
): Promise<{ downloadId: string; pinned: boolean; album: "image" | "ticket" | "gif" }> => {
  const e = normalizeOperatorEmail(viewerEmail);
  try {
    const response = await instance.put(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${encodeURIComponent(gameId)}/hub-showcase-pin-by-key`,
      { s3Key, pinned, album },
      { params: { viewerEmail: e || undefined } },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getGameHubDashboardRequest = async (
  gameId: string,
  viewerEmail: string,
  timeline?: {
    preset?: HubEngagementChartPreset | string;
    hubTimelineFrom?: number;
    hubTimelineTo?: number;
  },
): Promise<GameHubDashboardPayload> => {
  const e = normalizeOperatorEmail(viewerEmail);
  const params: Record<string, string> = { viewerEmail: e || "" };
  if (timeline?.preset) {
    params.hubTimelinePreset = timeline.preset;
  }
  if (timeline?.hubTimelineFrom != null) {
    params.hubTimelineFrom = String(timeline.hubTimelineFrom);
  }
  if (timeline?.hubTimelineTo != null) {
    params.hubTimelineTo = String(timeline.hubTimelineTo);
  }
  try {
    const response = await instance.get<GameHubDashboardPayload>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${encodeURIComponent(gameId)}/hub-dashboard`,
      { params },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getMoreHubShowcaseDownloadsRequest = async (
  gameId: string,
  viewerEmail: string,
  album: "image" | "ticket" | "gif",
  excludeIds: string[],
): Promise<{
  items: GameHubDashboardPayload["imageDownloadPool"];
  hasMore: boolean;
}> => {
  try {
    const response = await instance.post<{
      items: GameHubDashboardPayload["imageDownloadPool"];
      hasMore: boolean;
    }>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${encodeURIComponent(gameId)}/hub-showcase-downloads`,
      {
        viewerEmail: normalizeOperatorEmail(viewerEmail),
        album,
        excludeIds,
      },
    );
    return {
      items: response.data?.items || [],
      hasMore: !!response.data?.hasMore,
    };
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getGameChecklistRequest = async (
  gameId: string,
  viewerEmail: string,
): Promise<GameChecklistPayload> => {
  const e = normalizeOperatorEmail(viewerEmail);
  try {
    const response = await instance.get<GameChecklistPayload>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${gameId}/game-checklist`,
      { params: { viewerEmail: e } },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const submitGameChecklistRequest = async (
  gameId: string,
  body: SubmitGameChecklistBody,
): Promise<void> => {
  try {
    await instance.post(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${gameId}/game-checklist`,
      {
        ...body,
        viewerEmail: normalizeOperatorEmail(body.viewerEmail),
      },
    );
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export type PresignParkingReceiptResponse = {
  success: boolean;
  uploadKey: string;
  uploadURL: string;
  imageShortUrl: string;
};

export const presignGameParkingReceiptUpload = async (
  gameId: string,
  viewerEmail: string,
  fileMimeType: string,
): Promise<PresignParkingReceiptResponse> => {
  try {
    const response = await instance.post<PresignParkingReceiptResponse>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${gameId}/game-checklist/parking-receipt-presign`,
      {
        viewerEmail: normalizeOperatorEmail(viewerEmail),
        fileMimeType,
      },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const presignGameParkingSubmissionUpload = async (
  gameId: string,
  viewerEmail: string,
  fileMimeType: string,
): Promise<PresignParkingReceiptResponse> => {
  try {
    const response = await instance.post<PresignParkingReceiptResponse>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${gameId}/parking-submission/presign`,
      {
        viewerEmail: normalizeOperatorEmail(viewerEmail),
        fileMimeType,
      },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const submitGameParkingRequest = async (
  gameId: string,
  body: {
    viewerEmail: string;
    parkingCost?: number;
    parkingReceiptKey?: string;
    parkingReceiptUrl?: string;
    requestParkingExpenseEmail?: boolean;
  },
): Promise<void> => {
  try {
    await instance.post(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${gameId}/parking-submission`,
      {
        ...body,
        viewerEmail: normalizeOperatorEmail(body.viewerEmail),
      },
    );
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const presignGamePromotionPhotoUpload = async (
  gameId: string,
  viewerEmail: string,
  fileMimeType: string,
): Promise<PresignParkingReceiptResponse> => {
  try {
    const response = await instance.post<PresignParkingReceiptResponse>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${gameId}/game-checklist/promotion-photo-presign`,
      {
        viewerEmail: normalizeOperatorEmail(viewerEmail),
        fileMimeType,
      },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

/**
 * Super admin only: resend internal end-of-game checklist email to team list.
 */
export const resendGameChecklistReportEmailRequest = async (
  gameId: string,
  viewerEmail: string,
): Promise<void> => {
  const e = normalizeOperatorEmail(viewerEmail);
  try {
    await instance.post(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${gameId}/game-checklist/resend-report-email`,
      { viewerEmail: e },
    );
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const addGameRequest = async (request: GameInput): Promise<DocumentInput> => {
  try {
    const response = await instance.post<DocumentInput>(`${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}`, request);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateGameRequest = async (
  request: UpdateGameRequest,
  gameId: string,
  viewerEmail?: string | null,
): Promise<DocumentInput> => {
  try {
    const params = viewerEmail
      ? { viewerEmail: normalizeOperatorEmail(viewerEmail) }
      : {};
    const response = await instance.put<DocumentInput>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${gameId}`,
      request,
      { params },
    );

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const deleteGameRequest = async (gameId: string): Promise<boolean> => {
  try {
    await instance.delete<DocumentInput>(`${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${gameId}`);

    return true;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const changeGameStatusRequest = async (
  request: SetFinishDateRequest, gameId: string,
): Promise<boolean> => {
  try {
    const response = await instance.put<BasicResponse>(`${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.STATUS}/${gameId}`, request);

    return response.data.success;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const markOperatorGameLiveRequest = async (gameId: string): Promise<boolean> => {
  try {
    const response = await instance.put<BasicResponse>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.MARK_OPERATOR_GAME_LIVE}/${gameId}`,
      {},
    );
    return response.data.success;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateGameIsPaidGameRequest = async (
  isPaid: boolean, gameId: string,
): Promise<boolean> => {
  try {
    const request = {
      paidGame: isPaid || false,
    };
    const response = await instance.put<BasicResponse>(`${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.UPDATE_GAME_IS_PAID_GAME}/${gameId}`, request);

    return response.data.success;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateGameShowPrintOrderButtonRequest = async (
  showPrintOrderButton: boolean, gameId: string,
): Promise<boolean> => {
  try {
    const request = {
      showPrintOrderButton: showPrintOrderButton || false,
    };
    const response = await instance.put<BasicResponse>(`${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.UPDATE_GAME_SHOW_PRINT_ORDER_BUTTON}/${gameId}`, request);

    return response.data.success;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateGameCheckLastNameRequest = async (
  checkLastName: boolean, gameId: string,
): Promise<boolean> => {
  try {
    const request = {
      checkLastName: checkLastName || false,
    };
    const response = await instance.put<BasicResponse>(`${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.UPDATE_GAME_CHECK_LAST_NAME}/${gameId}`, request);

    return response.data.success;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateGameTemplateRequest = async (
  ticketTemplate: string, gameId: string,
): Promise<boolean> => {
  try {
    const request = {
      ticketTemplate: ticketTemplate || null,
    };
    const response = await instance.put<BasicResponse>(`${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.UPDATE_GAME_TICKET_TEMPLATE}/${gameId}`, request);

    return response.data.success;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateGameTemplatesRequest = async (
  ticketTemplate: string,
  fan1stTemplate: string,
  specialSectionTemplate: string,
  specialTemplate1: string,
  specialTemplate2: string,
  specialTemplate3: string,
  specialTemplate4: string,
  specialTemplate5: string,
  specialTemplate6: string,
  gameId: string,
): Promise<boolean> => {
  try {
    const request = {
      ticketTemplate: ticketTemplate || null,
      fan1stTicketTemplate: fan1stTemplate || null,
      specialSectionTemplate: specialSectionTemplate || null,
      specialTemplate1: specialTemplate1 || null,
      specialTemplate2: specialTemplate2 || null,
      specialTemplate3: specialTemplate3 || null,
      specialTemplate4: specialTemplate4 || null,
      specialTemplate5: specialTemplate5 || null,
      specialTemplate6: specialTemplate6 || null,
    };
    const response = await instance.put<BasicResponse>(`${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.UPDATE_GAME_TICKET_TEMPLATES}/${gameId}`, request);

    return response.data.success;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const updateGameSecondaryLogoColorDuoRequest = async (
  gameId: string,
  teamSecondaryLogoColorDuoId?: string | null,
  clearSelection?: boolean,
): Promise<boolean> => {
  try {
    const body: Record<string, string> = {};
    if (clearSelection) {
      body.deleteSecondaryLogo = "true";
    } else if (teamSecondaryLogoColorDuoId) {
      body.teamSecondaryLogoColorDuoId = teamSecondaryLogoColorDuoId;
    }
    const response = await instance.put<BasicResponse>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.UPDATE_GAME_SECONDARY_LOGO}/${gameId}`,
      body,
    );
    return response.data.success;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const assignOperatorsRequest = async (
  gameId: string,
  operators: string[],
  options?: { snapSquadArrivalTime?: string | null },
): Promise<{ assignedOperators: string[] }> => {
  try {
    const response = await instance.put<{
      assignedOperators: string[];
    }>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/assign-operators/${gameId}`,
      {
        operators,
        ...(options?.snapSquadArrivalTime != null
          ? { snapSquadArrivalTime: options.snapSquadArrivalTime }
          : {}),
      },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const assignGameManagerRequest = async (
  gameId: string,
  gameManagerEmails: string[] | null | undefined,
): Promise<{ gameManagers: string[]; gameManager: string | null }> => {
  try {
    const response = await instance.put<{
      gameManagers: string[];
      gameManager: string | null;
    }>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/assign-game-manager/${gameId}`,
      { gameManagers: gameManagerEmails ?? [] },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export interface SendPregameSmsResult {
  success: boolean;
  sent: number;
  skipped: number;
  noPhone: number;
  sendFailed?: number;
  enabled?: boolean;
  region?: string;
  originationNumber?: string | null;
  failures?: Array<{ phoneE164?: string; error?: string }>;
  recipients?: Array<{ phoneE164?: string; messageId?: string; status: string }>;
}

/** Super-admin: manually text all assigned operators for a game (MOSS test / on-demand). */
export const sendPregameSmsRequest = async (
  gameId: string,
  viewerEmail: string,
): Promise<SendPregameSmsResult> => {
  try {
    const response = await instance.post<SendPregameSmsResult>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/send-pregame-sms/${gameId}`,
      {},
      { params: { viewerEmail } },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export interface MomentNotifySubscriberRow {
  email: string;
  created: number;
  liveNotificationSentAt: number | null;
}

export interface MomentNotifyEmailPreview {
  subject: string;
  text: string;
  html: string;
  fromEmail: string;
  gameInfo: string;
}

export const getMomentNotifySubscribersRequest = async (
  gameId: string,
): Promise<MomentNotifySubscriberRow[]> => {
  try {
    const response = await instance.get(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.MOMENT_NOTIFY_SUBSCRIBERS}/${gameId}`,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getMomentNotifyEmailPreviewRequest = async (
  gameId: string,
): Promise<MomentNotifyEmailPreview> => {
  try {
    const response = await instance.get<MomentNotifyEmailPreview>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.MOMENT_NOTIFY_EMAIL_PREVIEW}/${gameId}`,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export interface MomentNotifyManualSendResult {
  sent: number;
  failed: Array<{ email: string; error: string }>;
  skippedNotOnList: string[];
}

export const sendMomentNotifyLiveEmailsRequest = async (
  gameId: string,
  emails: string[],
): Promise<MomentNotifyManualSendResult> => {
  try {
    const response = await instance.post<MomentNotifyManualSendResult>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.MOMENT_NOTIFY_SEND_EMAIL}/${gameId}`,
      { emails },
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getProfileReliveEmailPreviewRequest = async (
  gameId: string,
): Promise<MomentNotifyEmailPreview> => {
  try {
    const response = await instance.get<MomentNotifyEmailPreview>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.PROFILE_RELIVE_EMAIL_PREVIEW}/${gameId}`,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export interface ProfileReliveSendResult {
  sent: number;
  failed: Array<{ email: string; error: string }>;
}

export const sendProfileReliveEmailsRequest = async (
  gameId: string,
  teamId: string | undefined,
  body: { emails: string[]; subject: string; text: string; html: string },
): Promise<ProfileReliveSendResult> => {
  try {
    const qs = teamId ? stringify({ teamId }) : "";
    const response = await instance.post<ProfileReliveSendResult>(
      `${PATH.API}/${PATH.OPERATOR}/${PATH.GAME}/${PATH.PROFILE_RELIVE_EMAIL_SEND}/${gameId}${qs ? `?${qs}` : ""}`,
      body,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};
